/**
 * Default add-on protection is applied ONLY to successful add operations.
 * Rendering, opening the cart, quantity changes and removals never add a plan.
 */
(() => {
  'use strict';
  if (window.mulberryAddonProtection) return;

  const originalFetch = window.fetch.bind(window);
  const sources = new Set(['add-ons', 'addons', 'pdp_addons', 'pdp_add-ons', 'cart_drawer']);
  let mainVariantProvider = null;
  let queue = Promise.resolve();
  let active = 0;

  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;
  const isWarranty = (item) => Boolean(item?.properties?._warranty_offer_id || item?.properties?._product || item?.vendor === 'Mulberry');
  const variantId = (item) => String(item?.variant_id || item?.id || '');
  const linked = (cart, id) => (cart.items || []).filter(item =>
    isWarranty(item) && String(item.properties?._variantId || item.properties?._variant_id || '') === String(id)
  );
  const serial = (task) => {
    const next = queue.then(task, task);
    queue = next.catch(() => {});
    return next;
  };

  async function readCart() {
    const response = await originalFetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
    return response.json();
  }

  function parseItems(body) {
    if (!body) return [];
    if (typeof body === 'string') {
      try { return parseItems(JSON.parse(body)); } catch (_) { return parseItems(new URLSearchParams(body)); }
    }
    if (Array.isArray(body.items)) return body.items;
    if (typeof body.entries !== 'function') return body.id ? [body] : [];

    const items = new Map();
    const single = { properties: {} };
    for (const [name, value] of body.entries()) {
      if (typeof value !== 'string') continue;
      if (name === 'items') {
        try { return JSON.parse(value); } catch (_) { /* Continue with regular form fields. */ }
      }
      const match = name.match(/^items\[(\d+)\]\[(id|quantity|properties)\](?:\[([^\]]+)\])?$/);
      if (match) {
        const item = items.get(match[1]) || { properties: {} };
        if (match[2] === 'properties' && match[3]) item.properties[match[3]] = value;
        else item[match[2]] = value;
        items.set(match[1], item);
      } else if (name === 'id' || name === 'quantity') single[name] = value;
      else {
        const property = name.match(/^properties\[([^\]]+)\]$/);
        if (property) single.properties[property[1]] = value;
      }
    }
    return items.size ? [...items.values()] : single.id ? [single] : [];
  }

  function addonTargets(items) {
    const mainId = String(mainVariantProvider?.() || '');
    // A PDP batch explicitly containing the main variant can also contain
    // selected add-ons. Never assume unrelated, unmarked single adds are add-ons.
    const mainBatch = Boolean(mainId && items.length > 1 && items.some(item => variantId(item) === mainId));
    return [...new Set(items.filter(item => {
      const properties = item.properties || {};
      if (isWarranty(item) || String(properties._is_free_gift) === 'true') return false;
      if (!variantId(item) || Number(item.quantity ?? 1) <= 0) return false;
      if (mainId && variantId(item) === mainId) return false;
      const source = String(properties._added_from_location || '').toLowerCase();
      return sources.has(source) || mainBatch;
    }).map(variantId))];
  }

  function isAddRequest(input, init = {}) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, window.location.href);
      const method = String(init.method || input?.method || 'GET').toUpperCase();
      return method === 'POST' && url.origin === window.location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }

  async function requestItems(input, init) {
    if (init?.body != null) return parseItems(init.body);
    if (typeof input?.clone === 'function') return parseItems(await input.clone().text());
    return [];
  }

  function report(error, id) {
    console.warn(`Mulberry 3-year add-on protection was not added${id ? ` for variant ${id}` : ''}:`, error);
    document.dispatchEvent(new CustomEvent('mulberry-addon:error', { detail: { variantId: id, message: error.message } }));
  }

  async function ready() {
    for (let attempt = 0; attempt < 40; attempt++) {
      if (window.mulberry?.core?.getWarrantyOffer && window.mulberryShop?.getWarrantyVariant && window.mulberryShop?.addWarrantyToCart) {
        await window.mulberry.core.initCompleted;
        return;
      }
      await new Promise(resolve => window.setTimeout(resolve, 200));
    }
    throw new Error('Mulberry SDK is not ready. Product remains in the cart without automatic protection.');
  }

  async function changeLine(item, quantity, properties) {
    const response = await originalFetch(route('cart/change.js'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity, ...(properties ? { properties } : {}) })
    });
    if (!response.ok) throw new Error(`Unable to update add-on protection (${response.status}).`);
  }

  async function protect(id, before) {
    let cart = await readCart();
    const parents = cart.items.filter(item => !isWarranty(item) && variantId(item) === id);
    if (!parents.length) return;
    // The SDK associates by variant, not line key. Do not attach a warranty
    // across different lines of the same variant (possibly a main-product opt-out).
    if (parents.length !== 1) throw new Error('Multiple parent lines share this variant; automatic protection was skipped to preserve their choices.');
    const parent = parents[0];
    if (String(parent.properties?._is_free_gift) === 'true') return;
    if (/white[\s-]+glove/i.test(`${parent.title || ''} ${parent.handle || ''}`)) return;
    const existing = linked(cart, id);
    if (existing.length) {
      // Preserve any existing 3Y/5Y choice; only match its quantity to the parent.
      if (existing.length === 1 && existing[0].quantity !== parent.quantity) {
        await changeLine(existing[0], parent.quantity);
      }
      return;
    }
    // An existing unprotected parent can be a deliberate opt-out. Adding more
    // of that line is not permission to resurrect a removed warranty.
    if (before.items.some(item => !isWarranty(item) && variantId(item) === id)) return;

    await ready();
    if (!parent.handle) throw new Error('Add-on product handle is unavailable.');
    const productResponse = await originalFetch(route(`products/${encodeURIComponent(parent.handle)}.json`));
    if (!productResponse.ok) throw new Error('Unable to load add-on product data.');
    const { product } = await productResponse.json();
    const variant = product?.variants?.find(item => String(item.id) === id);
    if (!variant) throw new Error('Add-on variant is unavailable in product data.');
    const offers = await window.mulberry.core.getWarrantyOffer({
      id: variant.id, external_variant_id: variant.id,
      title: product.title, price: variant.price, detail: product
    });
    const offer = Array.isArray(offers) && offers.find(item => Number(item.duration_months) === 36 && Number.isFinite(Number(item.customer_cost ?? item.cost)) && Number(item.customer_cost ?? item.cost) >= 0);
    if (!offer) return; // Ineligible or no 3Y plan: never substitute another duration.
    const resolved = await window.mulberryShop.getWarrantyVariant(offer);
    if (!resolved?.shopify_variant_id) throw new Error(resolved?.error || 'Mulberry did not resolve a Shopify warranty variant.');

    // Recheck after network calls so removal or another explicit choice wins.
    cart = await readCart();
    const currentParent = cart.items.find(item => item.key === parent.key);
    if (!currentParent || linked(cart, id).length) return;
    await window.mulberryShop.addWarrantyToCart(resolved.shopify_variant_id, {
      title: product.title, variantId: id, quantity: currentParent.quantity
    }, offer, resolved.selling_plan_id);

    cart = await readCart();
    const warranty = linked(cart, id).find(item => String(item.variant_id) === String(resolved.shopify_variant_id) && Number(item.properties?._warranty_duration_months) === 36);
    if (!warranty) throw new Error('Shopify did not confirm the add-on warranty.');
    const label = currentParent.title || currentParent.product_title || product.title;
    if (warranty.quantity !== currentParent.quantity || warranty.properties?.Product !== label) {
      await changeLine(warranty, currentParent.quantity, { ...warranty.properties, Product: label });
    }
  }

  async function repairBundledSections(response) {
    // Shopify rendered these sections before the warranty existed. Return fresh
    // sections to the original product-form code without replacing its add flow.
    try {
      const data = await response.clone().json();
      const sections = data?.sections && Object.keys(data.sections);
      if (!sections?.length) return response;
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.join(','));
      const updated = await originalFetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!updated.ok) return response;
      const fresh = await updated.json();
      for (const section of sections) if (typeof fresh[section] === 'string') data.sections[section] = fresh[section];
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
    } catch (_) { return response; }
  }

  function begin() {
    active++;
    window.__mulberryAddonProtectionInFlight = true;
  }

  function end() {
    active = Math.max(0, active - 1);
    window.__mulberryAddonProtectionInFlight = active > 0;
    if (!active) document.dispatchEvent(new CustomEvent('mulberry-addon:complete'));
  }

  async function waitForManualProtection() {
    for (let attempt = 0; attempt < 100 && window.__mulberryStep3ProtectionMutationInFlight; attempt++) {
      await new Promise(resolve => window.setTimeout(resolve, 100));
    }
    if (window.__mulberryStep3ProtectionMutationInFlight) throw new Error('Another protection change is still running.');
  }

  window.fetch = async function mulberryAddonFetch(input, init) {
    if (!isAddRequest(input, init)) return originalFetch(input, init);
    let targets;
    try { targets = addonTargets(await requestItems(input, init)); }
    catch (_) { return originalFetch(input, init); }
    if (!targets.length) return originalFetch(input, init);

    return serial(async () => {
      let before;
      try {
        await waitForManualProtection();
        before = await readCart();
      } catch (error) {
        report(error);
        return originalFetch(input, init); // Never prevent the customer's product add.
      }
      begin();
      try {
        const response = await originalFetch(input, init);
        if (!response.ok) return response;
        for (const id of targets) {
          try { await protect(id, before); } catch (error) { report(error, id); }
        }
        return await repairBundledSections(response);
      } finally { end(); }
    });
  };

  // Older PDP add-on components use async XHR. Delay send until the
  // pre-add snapshot is known, then refresh the theme when protection finishes.
  const xhrOpen = window.XMLHttpRequest.prototype.open;
  const xhrSend = window.XMLHttpRequest.prototype.send;
  window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this.__mwAddonRequest = { method, url, async: rest[0] !== false };
    return xhrOpen.call(this, method, url, ...rest);
  };
  window.XMLHttpRequest.prototype.send = function(body) {
    const request = this.__mwAddonRequest;
    const targets = request?.async && isAddRequest(request.url, request) ? addonTargets(parseItems(body)) : [];
    if (!targets.length) return xhrSend.call(this, body);
    const xhr = this;
    serial(async () => {
      let before;
      try {
        await waitForManualProtection();
        before = await readCart();
      } catch (error) {
        report(error);
        if (xhr.readyState === 1) xhrSend.call(xhr, body);
        return;
      }
      // An abort while waiting must not turn into a later unexpected add.
      if (xhr.readyState !== 1) return;
      begin();
      try {
        await new Promise((resolve, reject) => {
          xhr.addEventListener('loadend', resolve, { once: true });
          try { xhrSend.call(xhr, body); } catch (error) { reject(error); }
        });
        if (xhr.status < 200 || xhr.status >= 300) return;
        for (const id of targets) {
          try { await protect(id, before); } catch (error) { report(error, id); }
        }
      } finally { end(); }
    }).catch(error => report(error));
  };

  window.mulberryAddonProtection = {
    setMainVariantProvider(provider) { mainVariantProvider = provider; },
    whenIdle() { return queue; },
    isBusy() { return active > 0; }
  };
})();
/* End shared add-on protection bootstrap. */

(() => {
  'use strict';

  const SELECTORS = {
    productPagePath: '/products/',
    form: 'form[action*="/cart/add"]',
    variantInput: '[name="id"]',
    submitButton:
      'button[name="add"], form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"]',
    inlineMount: '[data-mulberry-inline-mount]',
    inlineContainer: '.mulberry-inline-container',
    fallbackMount: '[id^="MainProduct-"]'
  };

  const state = {
    initialized: false,
    initPromise: null,
    form: null,
    submitButton: null,
    inlineContainer: null,
    product: null,
    offers: [],
    selectedInlineWarranty: null,
    inlineExplicitlyDeclined: false,
    offerRefreshToken: 0,
    bundleHandle: '',
    bundleVariantId: '',
    currentVariantId: '',
    variantTimer: null,
    prefetchConsumed: false,
    submitBypassOnce: false,
    submitInFlight: false,
    cartSyncInFlight: false,
    cartSyncQueued: false,
    lastSyncedCartSignature: ''
  };

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function getSettings() {
    return window.mulberry?.core?.settings || null;
  }

  function isMulberryReady() {
    return Boolean(window.mulberry?.core && window.mulberryShop && window.mulberry.core.settings);
  }

  function findProductForm() {
    return document.querySelector(SELECTORS.form);
  }

  function findSubmitButton(form) {
    if (!form) return null;
    return form.querySelector('button[name="add"], button[type="submit"], input[type="submit"]');
  }

  function getVariantInput() {
    return state.form?.querySelector(SELECTORS.variantInput) || null;
  }

  function getCurrentVariantId() {
    const formVariantId = getVariantInput()?.value;
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    return String(formVariantId || urlVariantId || '').trim();
  }

  function getSelectedVariant() {
    const variantId = getCurrentVariantId();

    if (variantId && window.mulberryShop) {
      window.mulberryShop.selectedVariantId = variantId;
    }

    return (
      state.product?.variants?.find((variant) => String(variant.id) === String(variantId)) ||
      state.product?.variants?.[0] ||
      null
    );
  }

  function ensureInlineContainer() {
    const scopedRoot = state.form?.closest(SELECTORS.fallbackMount) || document;

    let container =
      scopedRoot.querySelector(SELECTORS.inlineMount) ||
      scopedRoot.querySelector(SELECTORS.inlineContainer);

    if (!container) {
      container = document.createElement('div');
      container.className = 'mulberry-inline-container';

      if (state.submitButton?.parentElement) {
        state.submitButton.parentElement.insertAdjacentElement('beforebegin', container);
      } else {
        const fallbackMount = scopedRoot.querySelector(SELECTORS.fallbackMount) || document.querySelector(SELECTORS.fallbackMount);
        fallbackMount?.appendChild(container);
      }
    }

    container.classList.add('mulberry-inline-container');

    return container;
  }

  function productSkipsMulberry(product) {
    const tags = product?.tags;
    const list = Array.isArray(tags) ? tags : String(tags || '').split(',');
    return list.some((tag) => tag.trim().toLowerCase() === 'no-mulberry');
  }

  function setInlineVisibility(visible) {
    if (!state.inlineContainer) return;

    state.inlineContainer.classList.toggle('is-collapsed', !visible);
    state.inlineContainer.style.removeProperty('min-height');
    state.inlineContainer.style.removeProperty('display');
  }

  function clearInlineSelection() {
    state.selectedInlineWarranty = null;
    window.selectedOffer = null;
  }

  function setBusy(isBusy) {
    if (!state.submitButton) return;

    state.submitButton.classList.toggle('mw-apo-in-progress', isBusy);
    state.submitButton.setAttribute('aria-busy', String(isBusy));
    state.submitButton.disabled = isBusy;
  }

  function unwrapWarranty(warranty) {
    return warranty?.offer || warranty;
  }

  function getProtectedProductLabel(product, variant) {
    const productTitle = String(product?.title || '').trim();
    const variantTitle = String(variant?.title || '').trim();

    if (!variantTitle || variantTitle === 'Default Title') return productTitle;
    if (productTitle.toLowerCase().includes(variantTitle.toLowerCase())) return productTitle;

    return `${productTitle} - ${variantTitle}`;
  }

  async function ensureWarrantyProductProperty(parentVariantId, warrantyVariantId, offer, productLabel) {
    const label = String(productLabel || '').trim();
    if (!label) return;

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) return;

      const cart = await response.json();
      const offerId = String(offer?.id ?? offer?.offer_id ?? offer?.warranty_offer_id ?? '');
      const warrantyLine = (cart.items || []).find((item) => {
        const properties = item?.properties || {};
        const linkedVariantId = properties._variantId || properties._variant_id || properties.variant_id;
        if (String(linkedVariantId || '') !== String(parentVariantId)) return false;
        if (warrantyVariantId && String(item.variant_id) !== String(warrantyVariantId)) return false;
        if (offerId && properties._warranty_offer_id && String(properties._warranty_offer_id) !== offerId) return false;
        return item.vendor === 'Mulberry' || Boolean(properties._warranty_offer_id);
      });

      if (!warrantyLine) return;
      if (String(warrantyLine.properties?.Product || '').trim() === label) return;

      const changeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: warrantyLine.key,
          quantity: warrantyLine.quantity,
          properties: {
            ...(warrantyLine.properties || {}),
            Product: label
          }
        })
      });

      if (!changeResponse.ok) {
        console.warn('Mulberry protected-product label could not be saved to the warranty line.');
      }
    } catch (error) {
      console.warn('Mulberry protected-product label update failed:', error);
    }
  }

  function scheduleCartPlacementInitAndRender() {
    window.setTimeout(async () => {
      if (!window.mulberryCartPlacement) return;

      try {
        if (typeof window.mulberryCartPlacement.init === 'function') {
          await window.mulberryCartPlacement.init();
        }

        if (typeof window.mulberryCartPlacement.render === 'function') {
          await window.mulberryCartPlacement.render({ refresh: true });
        }
      } catch (error) {
        console.warn('Mulberry cart placement refresh failed:', error);
      }
    }, 100);
  }

  function canSyncMulberryCart() {
    return Boolean(
      window.mulberryShop &&
      typeof window.mulberryShop.removeDetachedWarranty === 'function' &&
      window.mulberryShop.cart &&
      typeof window.mulberryShop.cart.syncQuantities === 'function'
    );
  }

  function getCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';

    const lineState = cart.items
      .map((item) => `${item.key || item.id}:${item.quantity}`)
      .sort()
      .join('|');

    return `${cart.item_count || 0}::${cart.total_price || 0}::${lineState}`;
  }

  function getCartSectionsToRefresh() {
    const sections = [];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset?.id) {
      sections.push({
        section: mainCartItems.dataset.id,
        targetId: 'main-cart-items',
        selector: '.js-contents',
        targetSelector: '.js-contents'
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset?.id) {
      sections.push({
        section: mainCartFooter.dataset.id,
        targetId: 'main-cart-footer',
        selector: '.js-contents',
        targetSelector: '.js-contents'
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ section: 'cart-icon-bubble', targetId: 'cart-icon-bubble', selector: '.shopify-section' });
    }

    if (document.getElementById('cart-live-region-text')) {
      sections.push({ section: 'cart-live-region-text', targetId: 'cart-live-region-text', selector: '.shopify-section' });
    }

    if (document.querySelector('#CartDrawer')) {
      sections.push({ section: 'cart-drawer', targetSelector: '#CartDrawer', selector: '#CartDrawer' });
    }

    return sections;
  }

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  async function refreshCartUiAfterSync() {
    const sections = getCartSectionsToRefresh();
    if (!sections.length) return;

    const sectionList = sections.map((entry) => entry.section).join(',');
    const cartUrl = (typeof window.routes !== 'undefined' && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    const res = await fetch(`${cartUrl}?sections=${sectionList}`);
    if (!res.ok) return;

    const sectionData = await res.json();

    sections.forEach((entry) => {
      const target = entry.targetId
        ? document.getElementById(entry.targetId)
        : document.querySelector(entry.targetSelector);
      const html = sectionData?.[entry.section];
      if (!target || !html) return;

      const replacement = getSectionInnerHTML(html, entry.selector);
      if (!replacement) return;

      const destination = entry.targetSelector && entry.targetId
        ? target.querySelector(entry.targetSelector) || target
        : target;
      destination.innerHTML = replacement;
    });

    if (typeof window.emit === 'function') {
      window.emit('update:cart-line-item');
    }
  }

  async function removeAndSyncWarranties() {
    if (window.__mulberryAddonProtectionInFlight) return;
    if (!canSyncMulberryCart()) return;

    if (state.cartSyncInFlight) {
      state.cartSyncQueued = true;
      return;
    }

    state.cartSyncInFlight = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();


      const newCart = await window.mulberryShop.removeDetachedWarranty(cart);
      await window.mulberryShop.cart.syncQuantities(newCart);

      const currentSignature = getCartSignature(cart);
      // if (currentSignature && currentSignature === state.lastSyncedCartSignature) {
      //   return;
      // }

      const latestRes = await fetch('/cart.js');
      const latestCart = await latestRes.json();
      const latestSignature = getCartSignature(latestCart);

      state.lastSyncedCartSignature = latestSignature;

      if (latestSignature && latestSignature !== currentSignature) {
        await refreshCartUiAfterSync();
      }

      if (window.mulberryCartPlacement && typeof window.mulberryCartPlacement.refresh === 'function') {
        await window.mulberryCartPlacement.refresh();
      }
    } catch (err) {
      console.error('Cart sync error:', err);
    } finally {
      state.cartSyncInFlight = false;

      if (state.cartSyncQueued) {
        state.cartSyncQueued = false;
        removeAndSyncWarranties();
      }
    }
  }

  window.mulberryRemoveAndSyncWarranties = removeAndSyncWarranties;

  async function getOffers() {
    const productUrl = state.bundleHandle
      ? `/products/${state.bundleHandle}`
      : window.mulberryShop.getProductUrl();

    const productData = await window.mulberryShop.getProductData(productUrl);
    const nextProduct = productData?.product;

    if (!nextProduct) {
      return [];
    }

    state.product = nextProduct;

    const variantIdFromState = getCurrentVariantId();
    const resolvedVariantId = variantIdFromState || state.bundleVariantId;

    if (resolvedVariantId) {
      window.mulberryShop.selectedVariantId = String(resolvedVariantId);
      window.selectedVariantId = String(resolvedVariantId);
    }

    let selectedVariant =
      nextProduct.variants?.find((variant) => String(variant.id) === String(window.mulberryShop.selectedVariantId)) ||
      nextProduct.variants?.[0] ||
      null;

    if (!selectedVariant || !selectedVariant.id || !selectedVariant.price) {
      return [];
    }

    const fetchedOffers = await window.mulberry.core.getWarrantyOffer({
      detail: { ...nextProduct, id: selectedVariant.id },
      title: nextProduct.title,
      id: selectedVariant.id,
      price: selectedVariant.price
    });

    return Array.isArray(fetchedOffers) ? fetchedOffers : [];
  }

  function ensureCustomProtectionStyles() {
    if (document.getElementById('mulberry-custom-protection-styles')) return;

    const style = document.createElement('style');
    style.id = 'mulberry-custom-protection-styles';
    style.textContent = `
      .mulberry-inline-container {
        width: 100%;
        min-height: 168px;
        margin: 0 0 24px;
        font-family: inherit;
      }

      .mulberry-inline-container.is-collapsed {
        display: none !important;
        min-height: 0 !important;
        margin: 0 !important;
      }

      .mulberry-custom-protection {
        width: 100%;
        color: #111111;
      }

      .mulberry-custom-protection__header {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 10px 28px;
        margin-bottom: 10px;
      }

      .mulberry-custom-protection__title {
        margin: 0;
        font: inherit;
        font-size: 20px;
        line-height: 1.25;
        font-weight: 700;
        color: #111111;
      }

      .mulberry-custom-protection__faq {
        appearance: none;
        -webkit-appearance: none;
        padding: 0;
        margin: 0;
        border: 0;
        background: transparent;
        color: #111111;
        font: inherit;
        font-size: 18px;
        line-height: 1.25;
        text-decoration: underline;
        text-underline-offset: 3px;
        cursor: pointer;
      }

      .mulberry-custom-protection__description {
        margin: 0 0 22px;
        font-size: 16px;
        line-height: 1.45;
        color: #2f2f2f;
      }

      .mulberry-custom-protection__options {
        display: grid;
        grid-template-columns: repeat(var(--mulberry-plan-count, 2), minmax(0, 1fr));
        width: 100%;
        overflow: hidden;
        border: 2px solid #dedede;
        border-radius: 16px;
        background: #ffffff;
        padding: 0 !important;
        gap: 0 !important;
        align-items: stretch !important;
      }

      .mulberry-custom-protection__option {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
        min-height: 82px;
        height: 100%;
        padding: 16px 22px;
        margin: 0 !important;
        align-self: stretch !important;
        box-sizing: border-box;
        cursor: pointer;
        background: #ffffff;
        color: #111111;
        transition: box-shadow .15s ease, color .15s ease, background-color .15s ease;
      }

      .mulberry-custom-protection__option + .mulberry-custom-protection__option {
        border-left: 1px solid #dedede;
      }

      .mulberry-custom-protection__option.is-selected {
        z-index: 1;
        color: #111111;
        box-shadow: inset 0 0 0 2px #2B3974;
      }

      .mulberry-custom-protection__option:first-child.is-selected {
        border-radius: 14px 0 0 14px;
      }

      .mulberry-custom-protection__option:last-child.is-selected {
        border-radius: 0 14px 14px 0;
      }

      .mulberry-custom-protection__input {
        position: absolute;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }

      .mulberry-custom-protection__option-content {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 14px;
        min-width: 0;
      }

      .mulberry-custom-protection__radio {
        position: relative;
        width: 24px;
        height: 24px;
        flex: 0 0 24px;
        border: 3px solid #6652A1;
        border-radius: 50%;
        box-sizing: border-box;
      }

      .mulberry-custom-protection__option.is-selected .mulberry-custom-protection__radio::after {
        content: '';
        position: absolute;
        inset: 4px;
        border-radius: 50%;
        background: #6652A1;
      }

      .mulberry-custom-protection__plan-text {
        min-width: 0;
        font-size: 18px;
        line-height: 1.25;
        white-space: nowrap;
      }

      .mulberry-custom-protection__price {
        font-weight: 700;
        color: #2d427a;
      }

      @media (max-width: 749px) {
        .mulberry-inline-container {
          min-height: 220px;
          margin-bottom: 18px;
        }

        .mulberry-custom-protection__header {
          gap: 7px 18px;
          margin-bottom: 8px;
        }

        .mulberry-custom-protection__title {
          font-size: 17px;
        }

        .mulberry-custom-protection__faq {
          font-size: 15px;
        }

        .mulberry-custom-protection__description {
          margin-bottom: 16px;
          font-size: 14px;
        }

        .mulberry-custom-protection__options {
          grid-template-columns: 1fr;
          border-radius: 12px;
        }

        .mulberry-custom-protection__option {
          min-height: 64px;
          padding: 13px 16px;
        }

        .mulberry-custom-protection__option + .mulberry-custom-protection__option {
          border-left: 0;
          border-top: 1px solid #dedede;
        }

        .mulberry-custom-protection__option:first-child.is-selected,
        .mulberry-custom-protection__option:last-child.is-selected,
        .mulberry-custom-protection__option.is-selected {
          border-radius: 10px;
        }

        .mulberry-custom-protection__option-content {
          gap: 10px;
        }

        .mulberry-custom-protection__radio {
          width: 20px;
          height: 20px;
          flex-basis: 20px;
          border-width: 2px;
        }

        .mulberry-custom-protection__option.is-selected .mulberry-custom-protection__radio::after {
          inset: 4px;
        }

        .mulberry-custom-protection__plan-text {
          font-size: 15px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getOfferDurationYears(offer) {
    const months = Number(offer?.duration_months);
    if (Number.isFinite(months) && months > 0) {
      return months / 12;
    }

    const years = Number(offer?.duration_years || offer?.years);
    if (Number.isFinite(years) && years > 0) {
      return years;
    }

    return '';
  }

  function getOfferCost(offer) {
    const value =
      offer?.cost ??
      offer?.customer_cost ??
      offer?.price ??
      offer?.warranty_price;

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function formatOfferPrice(offer) {
    const amount = getOfferCost(offer);
    if (amount == null) return '';

    const currency = String(
      offer?.currency ||
      offer?.currency_code ||
      'USD'
    ).toUpperCase();

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function getOfferKey(offer, index = 0) {
    return String(
      offer?.id ??
      offer?.offer_id ??
      offer?.warranty_offer_id ??
      `offer-${index}`
    );
  }

  function setSelectedOffer(offer, explicitChoice = false) {
    if (explicitChoice) state.inlineExplicitlyDeclined = !offer;
    state.selectedInlineWarranty = offer || null;
    window.selectedOffer = state.selectedInlineWarranty;
  }

  function updateCustomSelectionUi() {
    if (!state.inlineContainer) return;

    const selected = state.selectedInlineWarranty;
    const selectedIndex = state.offers.findIndex((offer) => offer === selected);

    state.inlineContainer
      .querySelectorAll('[data-mulberry-custom-option]')
      .forEach((option, index) => {
        const isSelected = index === selectedIndex;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-checked', String(isSelected));

        const input = option.querySelector('input[type="radio"]');
        if (input) input.checked = isSelected;
      });
  }

  async function openCustomFaq() {
    const offer = state.selectedInlineWarranty || state.offers[0];
    const settings = getSettings();

    if (!offer || !settings || !window.mulberry?.faq?.init || !window.mulberry?.faq?.open) {
      return;
    }

    try {
      await Promise.resolve(window.mulberry.faq.init({
        coverage: offer,
        settings
      }));
      window.mulberry.faq.open();
    } catch (error) {
      console.warn('Mulberry FAQ open failed:', error);
    }
  }

  function renderCustomProtection(offers) {
    if (!state.inlineContainer) return;

    const nextOffers = Array.isArray(offers) ? offers : [];

    if (!nextOffers.length) {
      state.inlineContainer.innerHTML = '';
      setInlineVisibility(false);
      clearInlineSelection();
      return;
    }

    const previousKey = state.selectedInlineWarranty
      ? getOfferKey(state.selectedInlineWarranty)
      : '';

    const previousSelection = nextOffers.find(
      (offer, index) => getOfferKey(offer, index) === previousKey
    );

    setSelectedOffer(state.inlineExplicitlyDeclined ? null : previousSelection || nextOffers[0]);
    setInlineVisibility(true);

    const optionsHtml = nextOffers.map((offer, index) => {
      const years = getOfferDurationYears(offer);
      const price = formatOfferPrice(offer);
      const durationLabel = years
        ? `${escapeHtml(years)} Year`
        : escapeHtml(offer?.name || 'Protection');

      return `
        <label
          class="mulberry-custom-protection__option"
          data-mulberry-custom-option
          data-offer-index="${index}"
          role="radio"
          aria-checked="false"
        >
          <input
            class="mulberry-custom-protection__input"
            type="radio"
            name="mulberry-custom-protection"
            value="${escapeHtml(getOfferKey(offer, index))}"
          >
          <span class="mulberry-custom-protection__option-content">
            <span class="mulberry-custom-protection__radio" aria-hidden="true"></span>
            <span class="mulberry-custom-protection__plan-text">
              ${durationLabel}${price ? ` - <span class="mulberry-custom-protection__price">${escapeHtml(price)}</span>` : ''}
            </span>
          </span>
        </label>
      `;
    }).join('');

    state.inlineContainer.innerHTML = `
      <div class="mulberry-custom-protection" data-mulberry-custom-protection>
        <div class="mulberry-custom-protection__header">
          <h3 class="mulberry-custom-protection__title">Protect your product</h3>
          <button class="mulberry-custom-protection__faq" type="button" data-mulberry-custom-faq>
            What's covered
          </button>
        </div>

        <p class="mulberry-custom-protection__description">
          Easy Claims online, fast replacements, no fee
        </p>

        <div
          class="mulberry-custom-protection__options"
          style="--mulberry-plan-count: ${Math.max(nextOffers.length, 1)}"
          role="radiogroup"
          aria-label="Protection plan"
        >
          ${optionsHtml}
        </div>
      </div>
    `;

    state.inlineContainer
      .querySelectorAll('[data-mulberry-custom-option]')
      .forEach((option) => {
        const input = option.querySelector('input[type="radio"]');
        if (!input) return;

        input.addEventListener('click', () => {
          const index = Number(option.dataset.offerIndex);
          const selectedOffer = state.offers[index];
          if (!selectedOffer) return;

          const alreadySelected =
            state.selectedInlineWarranty === selectedOffer;

          setSelectedOffer(alreadySelected ? null : selectedOffer, true);
          updateCustomSelectionUi();
        });
      });

    state.inlineContainer
      .querySelector('[data-mulberry-custom-faq]')
      ?.addEventListener('click', openCustomFaq);

    updateCustomSelectionUi();
  }

  function selectFirstOffer(offers) {
    const nextOffers = Array.isArray(offers) ? offers : [];

    if (!nextOffers.length) {
      clearInlineSelection();
      return;
    }

    setSelectedOffer(nextOffers[0]);
  }

  function applyInlineOffers(offers) {
    const nextOffers = Array.isArray(offers) ? offers : [];

    state.offers = nextOffers;
    renderCustomProtection(nextOffers);
  }

  async function addWarrantyBeforeProduct(warranty) {
    const offer = unwrapWarranty(warranty);
    if (!offer) {
      throw new Error('No Mulberry warranty offer selected.');
    }

    const selectedVariant = getSelectedVariant();
    if (!selectedVariant) {
      throw new Error('Unable to identify selected Shopify variant.');
    }

    window.mulberryShop.selectedVariantId = String(selectedVariant.id);

    const warrantyVariant = await window.mulberryShop.getWarrantyVariant(offer);

    if (!warrantyVariant?.shopify_variant_id) {
      throw new Error('Mulberry did not return a Shopify warranty variant.');
    }

    await window.mulberryShop.addWarrantyToCart(
      warrantyVariant.shopify_variant_id,
      {
        title: state.product.title,
        variantId: String(selectedVariant.id),
        quantity: Math.max(1, Number(state.form?.querySelector('[name="quantity"]')?.value) || 1)
      },
      offer,
      warrantyVariant.selling_plan_id
    );

    await ensureWarrantyProductProperty(
      selectedVariant.id,
      warrantyVariant.shopify_variant_id,
      offer,
      getProtectedProductLabel(state.product, selectedVariant)
    );

    scheduleCartPlacementInitAndRender();
  }

  function submitProductOnce() {
    if (!state.form) return;

    state.submitBypassOnce = true;

    if (typeof state.form.requestSubmit === 'function') {
      if (state.submitButton && state.submitButton.form === state.form) {
        state.form.requestSubmit(state.submitButton);
      } else {
        state.form.requestSubmit();
      }
      return;
    }

    HTMLFormElement.prototype.submit.call(state.form);
  }

  async function onFormSubmit(event) {
    if (state.submitBypassOnce) {
      state.submitBypassOnce = false;
      return;
    }

    if (state.submitInFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!state.selectedInlineWarranty) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    state.submitInFlight = true;
    setBusy(true);

    try {
      await addWarrantyBeforeProduct(state.selectedInlineWarranty);
    } catch (error) {
      console.error('Mulberry warranty add failed. Continuing with product add.', error);
    } finally {
      setBusy(false);
      state.submitInFlight = false;
      submitProductOnce();
    }
  }

  async function initInline() {
    if (productSkipsMulberry(state.product)) {
      const scopedRoot = state.form?.closest(SELECTORS.fallbackMount) || document;
      scopedRoot.querySelector(SELECTORS.inlineMount)?.remove();
      return;
    }

    state.inlineContainer = ensureInlineContainer();
    ensureCustomProtectionStyles();
    applyInlineOffers(state.offers);
  }

  async function refreshOffers(force = false) {
    if (!state.product) return;

    const nextVariantId = getCurrentVariantId();
    if (!nextVariantId) return;

    if (!force && nextVariantId === state.currentVariantId) {
      return;
    }

    state.currentVariantId = nextVariantId;
    const token = ++state.offerRefreshToken;

    await wait(150);
    if (token !== state.offerRefreshToken) return;

    const offers = await getOffers();
    if (token !== state.offerRefreshToken || nextVariantId !== getCurrentVariantId()) return;
    applyInlineOffers(offers);
  }

  function scheduleOfferRefresh(force = false) {
    window.clearTimeout(state.variantTimer);
    state.variantTimer = window.setTimeout(() => {
      refreshOffers(force).catch((error) => {
        console.error('Mulberry variant refresh failed:', error);
      });
    }, 120);
  }

  function watchVariantChanges() {
    state.form?.addEventListener('change', (event) => {
      if (event.target.closest?.('[data-mulberry-custom-protection]')) return;
      scheduleOfferRefresh(true);
    }, true);

    document.addEventListener('variant:swatch', () => {
      scheduleOfferRefresh(true);
    });

    document.addEventListener('mulberry-shopify:variant-change', () => {
      scheduleOfferRefresh(true);
    });

    document.addEventListener('bundle-builder:mode-change', (event) => {
      const mode = event?.detail?.mode || '';

      if (mode !== 'bundle') {
        state.bundleHandle = '';
        state.bundleVariantId = '';
      }

      scheduleOfferRefresh(true);
    });

    document.addEventListener('bundle-builder:bundle-change', (event) => {
      const detail = event?.detail || {};
      const mode = detail?.mode || '';

      if (mode && mode !== 'bundle') {
        state.bundleHandle = '';
        state.bundleVariantId = '';
        scheduleOfferRefresh(true);
        return;
      }

      state.bundleHandle = detail.handle || '';
      state.bundleVariantId =
        detail?.config?.variantId ||
        detail?.config?.selectedVariantId ||
        '';

      scheduleOfferRefresh(true);
    });
  }

  function attachSubmitHandler() {
    state.form.removeEventListener('submit', onFormSubmit, true);
    state.form.addEventListener('submit', onFormSubmit, true);
  }

  async function takeInitialPrefetch() {
    const pending = window.__ttMulberryPrefetch;
    if (!pending || state.prefetchConsumed) return null;
    state.prefetchConsumed = true;
    try {
      const result = await pending;
      if (!result || !result.product || !Array.isArray(result.offers)) return null;
      if (String(result.variantId) !== String(getCurrentVariantId())) return null;
      return result;
    } catch (_) {
      return null;
    }
  }

  async function initMulberryPdp() {
    if (state.initialized) return;

    state.form = findProductForm();
    state.submitButton = findSubmitButton(state.form);

    if (!state.form || !state.submitButton) {
      console.warn('Mulberry PDP: product form or Add to Cart button not found.');
      return;
    }

    const prefetched = await takeInitialPrefetch();
    if (prefetched) {
      state.product = prefetched.product;
    } else {
      const response = await window.mulberryShop.getProductData(window.mulberryShop.getProductUrl());
      state.product = response?.product;
    }

    if (!state.product) {
      throw new Error('Mulberry PDP: product data unavailable.');
    }

    window.mulberryAddonProtection?.setMainVariantProvider(getCurrentVariantId);
    state.currentVariantId = getCurrentVariantId();
    state.offers = prefetched ? prefetched.offers : await getOffers();

    await initInline();

    attachSubmitHandler();
    watchVariantChanges();

    state.initialized = true;
  }

  async function boot() {
    if (!window.location.pathname.includes(SELECTORS.productPagePath)) {
      return;
    }

    if (state.initialized || state.initPromise) {
      return;
    }

    state.initPromise = (async () => {
      if (!isMulberryReady()) {
        console.warn('Mulberry is not ready when mulberry-shopify:loaded fired.');
        return;
      }

      if (window.mulberry?.core?.initCompleted && typeof window.mulberry.core.initCompleted.then === 'function') {
        await window.mulberry.core.initCompleted.catch(() => {});
      }

      await initMulberryPdp();
    })();

    try {
      await state.initPromise;
    } catch (error) {
      console.error('Mulberry PDP initialization failed:', error);
    } finally {
      state.initPromise = null;
    }
  }

  function startWhenReady() {
    const start = () => {
      // Cart cleanup must not hold up the offer block. The head prefetch
      // already started the offer request while the page was parsing.
      removeAndSyncWarranties();
      boot();
    };
    if (isMulberryReady()) start();
    else document.addEventListener('mulberry-shopify:loaded', start, { once: true });
  }

  startWhenReady();
})();